import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import LogoutModal from '../components/common/LogoutModal';
import { useDispatch, useSelector } from 'react-redux';
import { DocumnetTextIcon, MobileMenuIcon, MobileSearchIcon } from '../assets/svgs/svgs';
import { fetchTopics } from '../store/actions/topicsActions';
import { getUserInitials, getUserDisplayName } from '../utils/userUtils';
import { logoutUser } from '../store/actions/authActions';

const TopicLibrary = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [scriptsPage, setScriptsPage] = useState(1);
  const itemsPerPage = 4;
  const { scripts, loading, error } = useSelector((state) => {
    return state.topics;
  });
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  
  useEffect(() => {
    dispatch(fetchTopics());
  }, [dispatch]);

  const libraryScripts = React.useMemo(() => {
    const scriptsArray = Array.isArray(scripts) ? scripts : [];
    
    return scriptsArray.map((script) => {
      const authorName = script.userId?.fullName || script.user?.fullName || 'Unknown';
      const authorProfilePicture = script.userId?.googleOAuth?.profilePicture || script.user?.profilePicture || null;

      const scriptTitle = script?.topic?.title || script?.title || 'Untitled Script';
      const scriptDescription = script?.sequence?.hook?.text || script?.topic?.description || script.content?.substring(0, 150) || 'No description available';

      return {
        id: script._id || script.id,
        title: scriptTitle,
        description: scriptDescription,
        author: authorName,
        authorProfilePicture: authorProfilePicture,
        authorUser: script.userId || script.user,
        ...script
      };
    });
  }, [scripts]);


  const handleViewScript = (script) => {
    navigate('/script-details', {
      state: { script, scriptId: script?.id || script?._id, fromPage: 'topic-library' }
    });
  };




  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    dispatch(logoutUser());
    navigate('/signin');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const filteredScripts = libraryScripts.filter(script =>
    (script.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (script.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalScriptsPages = Math.ceil(filteredScripts.length / itemsPerPage);

  const paginatedScripts = filteredScripts.slice(
    (scriptsPage - 1) * itemsPerPage,
    scriptsPage * itemsPerPage
  );

  useEffect(() => {
    setScriptsPage(1);
  }, [searchQuery]);

  return (
    <div className="h-screen bg-main flex overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 overlay-dark z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activePage="topic-library"
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <header className="bg-primary text-white py-4 px-6 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <MobileMenuIcon/>
            </button>

            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search scripts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full text-white placeholder:text-white/70 px-4 py-2.5 pr-10 rounded-lg border border-white bg-transparent focus:outline-none text-sm transition-all"
              />
              <MobileSearchIcon className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 pointer-events-none"/>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 border-white/30">
                {getUserInitials(user)}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{getUserDisplayName(user)}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-black">Shared Scripts</h1>
            <p className="text-secondary text-sm mt-1">Here are all scripts shared by other creators.</p>
          </div>

          {loading && (
            <div className="bg-white rounded-3xl p-12 text-center shadow-lg">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-secondary">Loading Shared Scripts...</p>
            </div>
          )}

          {error && !loading && (
            <div className="bg-white rounded-3xl p-12 text-center shadow-lg">
              <p className="text-red-500 mb-2">Error loading topics</p>
              <p className="text-secondary text-sm">{error}</p>
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-8">
              {/* Shared Scripts Section */}
              {filteredScripts.length > 0 && (
                <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-lg">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {paginatedScripts.map((script) => {
                      const scriptId = script?.id || script?._id;

                      return (
                        <div
                          key={scriptId}
                          className="bg-[#F5EBE0] rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => handleViewScript(script)}
                        >
                          {/* Top section - Profile pic left, Author initials+name right */}
                          <div className="flex justify-between items-start mb-4">
                            {/* Profile Picture - Left */}
                            <div className="w-36 h-36 rounded-lg overflow-hidden flex-shrink-0">
                              {script.authorProfilePicture ? (
                                <img
                                  src={script.authorProfilePicture}
                                  alt={script.author}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                                  <span className="text-3xl font-bold text-amber-600">
                                    {script.author?.charAt(0)?.toUpperCase() || 'U'}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Author initials + name - top right */}
                            <div className="flex items-center gap-2">
                              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-semibold text-gray-700 border-2 border-gray-500">
                                {script.author?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U'}
                              </div>
                              <span className="text-sm text-gray-700 font-medium">{script.author}</span>
                            </div>
                          </div>

                          {/* Title and description */}
                          <div className="mb-5">
                            <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1">{script.title}</h3>
                            <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">{script.description}</p>
                          </div>

                          {/* View Scripts Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewScript(script);
                            }}
                            className="w-full py-3 bg-[#78350F] text-white rounded-[10px] font-semibold hover:bg-[#5A2709] transition-colors text-sm"
                          >
                            View Scripts
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Scripts Pagination */}
                  {filteredScripts.length > itemsPerPage && (
                    <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => setScriptsPage(prev => Math.max(1, prev - 1))}
                        disabled={scriptsPage === 1}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {scriptsPage} of {totalScriptsPages}
                      </span>
                      <button
                        onClick={() => setScriptsPage(prev => Math.min(totalScriptsPages, prev + 1))}
                        disabled={scriptsPage >= totalScriptsPages}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}

              {filteredScripts.length === 0 && (
                <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                  <DocumnetTextIcon/>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No shared scripts found</h3>
                  <p className="text-secondary mb-6">Try adjusting your search query or check back later</p>
                </div>
              )}
            </div>
          )}
          <LogoutModal 
            showLogoutModal={showLogoutModal}
            onConfirm={confirmLogout}
            onCancel={cancelLogout}
          />
        </main>
      </div>
    </div>
  );
};

export default TopicLibrary;